import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Flame,
    RefreshCw,
    Trophy,
    TrendingUp,
    AlertTriangle,
} from "lucide-react";
import { BrandBadge } from "../components/BrandBadge";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { BOOKMAKERS, getBookmakerBySlug } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OddCell = ({ value, isBest }) => {
    if (value === null || value === undefined) {
        return (
            <div className="text-center text-gray-500 text-xs">—</div>
        );
    }
    return (
        <div
            className={`px-2 py-1.5 rounded-md text-center min-w-[52px] font-heading font-bold text-sm tabular-nums transition-colors ${
                isBest
                    ? "bg-[#00C853] text-black"
                    : "bg-[#1E3A5F] text-white"
            }`}
        >
            {value.toFixed(2)}
        </div>
    );
};

const Cotes = () => {
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bookmakerFilter, setBookmakerFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/odds`)
            .then(({ data }) => {
                if (!cancelled) {
                    setMatches(data);
                    setLoading(false);
                }
            })
            .catch(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, []);

    const grouped = useMemo(() => {
        const g = {};
        for (const m of matches) {
            if (!g[m.competition]) g[m.competition] = [];
            g[m.competition].push(m);
        }
        return g;
    }, [matches]);

    // Apply bookmaker filter: keep only the rows for the selected book inside each match
    const visibleMatches = useMemo(() => {
        if (bookmakerFilter === "all") return matches;
        return matches
            .map((m) => ({
                ...m,
                odds: m.odds.filter((o) => o.bookmaker === bookmakerFilter),
                totals: (m.totals || []).filter(
                    (t) => t.bookmaker === bookmakerFilter,
                ),
            }))
            .filter((m) => m.odds.length > 0);
    }, [matches, bookmakerFilter]);

    const visibleGrouped = useMemo(() => {
        const g = {};
        for (const m of visibleMatches) {
            if (!g[m.competition]) g[m.competition] = [];
            g[m.competition].push(m);
        }
        return g;
    }, [visibleMatches]);

    const competitionOrder = [
        "Ligue 1",
        "Champions League",
        "Premier League",
        "La Liga",
        "Bundesliga",
        "Serie A",
        "Coupe du Monde",
    ];

    return (
        <div data-testid="cotes-page">
            <SEO
                title="Comparateur de cotes foot — Ligue 1, Premier League, La Liga | Pronospot.com"
                description="Comparez en direct les meilleures cotes 1N2 et Over/Under 2.5 buts sur la Ligue 1, Champions League, Premier League, La Liga, Bundesliga et Serie A. Bookmakers agréés ANJ."
                jsonLd={breadcrumbSchema([
                    { name: "Accueil", url: "https://pronospot.com/" },
                    {
                        name: "Cotes du jour",
                        url: "https://pronospot.com/cotes",
                    },
                ])}
            />

            <section className="container-x pt-10 pb-6">
                <Breadcrumbs items={[{ label: "Cotes du jour" }]} />
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <Flame size={20} className="text-[#FFB300]" />
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E63946]/15 border border-[#E63946]/40 text-[10px] font-bold text-[#E63946] uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] animate-pulse" />
                        Live
                    </span>
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter">
                    Comparateur de cotes foot
                </h1>
                <p className="text-gray-400 mt-4 max-w-3xl text-lg">
                    Les meilleures cotes <strong className="text-white">1N2</strong> et{" "}
                    <strong className="text-white">Over/Under 2.5 buts</strong>{" "}
                    en direct sur 7 compétitions, comparées entre Winamax,
                    Betclic, Unibet, ZEbet, Vbet et Parions Sport. Cote la
                    plus haute mise en vert.
                </p>
            </section>

            {/* Bookmaker filter */}
            <section
                className="container-x pb-8"
                data-testid="bookmaker-filter-section"
            >
                <div className="card-elev p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp size={16} className="text-[#00C853]" />
                        <span className="font-heading font-semibold text-white text-sm uppercase tracking-wider">
                            Sélectionner un bookmaker
                        </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setBookmakerFilter("all")}
                            data-testid="bm-filter-all"
                            className={`text-sm px-4 py-2 rounded-full border transition-all ${
                                bookmakerFilter === "all"
                                    ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                    : "bg-transparent text-gray-300 border-[#2A4A6B] hover:border-[#00C853]"
                            }`}
                        >
                            Tous les books
                        </button>
                        {BOOKMAKERS.map((b) => (
                            <button
                                key={b.slug}
                                onClick={() => setBookmakerFilter(b.slug)}
                                data-testid={`bm-filter-${b.slug}`}
                                className={`text-sm px-4 py-2 rounded-full border transition-all ${
                                    bookmakerFilter === b.slug
                                        ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                        : "bg-transparent text-gray-300 border-[#2A4A6B] hover:border-[#00C853]"
                                }`}
                            >
                                {b.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Matches */}
            <section className="container-x pb-20">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm">
                        <RefreshCw size={16} className="animate-spin" />
                        Chargement des cotes en direct...
                    </div>
                ) : matches.length === 0 ? (
                    <div className="card-elev p-10 text-center" data-testid="no-matches">
                        <AlertTriangle
                            className="text-[#FFB300] mx-auto mb-4"
                            size={32}
                        />
                        <h3 className="font-heading font-bold text-white text-xl mb-2">
                            Aucun match disponible
                        </h3>
                        <p className="text-gray-400 text-sm">
                            La période actuelle ne dispose pas de matchs
                            programmés. Revenez bientôt !
                        </p>
                    </div>
                ) : visibleMatches.length === 0 ? (
                    <div className="card-elev p-10 text-center">
                        <p className="text-gray-400">
                            Ce bookmaker ne propose aucune cote pour les
                            matchs actuels.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-12">
                        {competitionOrder
                            .filter((c) => visibleGrouped[c]?.length)
                            .map((comp) => (
                                <div
                                    key={comp}
                                    data-testid={`cotes-section-${comp.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="flex items-center justify-between mb-5">
                                        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white flex items-center gap-3 tracking-tight">
                                            <Trophy
                                                className="text-[#FFB300]"
                                                size={22}
                                            />
                                            {comp}
                                            <span className="text-sm font-normal text-gray-500">
                                                · {visibleGrouped[comp].length} match
                                                {visibleGrouped[comp].length > 1
                                                    ? "s"
                                                    : ""}
                                            </span>
                                        </h2>
                                    </div>

                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                        {visibleGrouped[comp].map((m) => (
                                            <MatchCard
                                                key={m.match_id}
                                                match={m}
                                                allMatches={grouped[comp] || []}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                    </div>
                )}

                <p className="text-[11px] text-gray-500 mt-10 text-center max-w-2xl mx-auto">
                    Cotes fournies par The Odds API, agrégées toutes les
                    10 minutes. 18+ | Jouer comporte des risques. Vérifiez
                    toujours la cote en temps réel sur le site de l'opérateur
                    avant de placer un pari.
                </p>
            </section>
        </div>
    );
};

const MatchCard = ({ match, allMatches }) => {
    // We use "allMatches" to compute the best across the full unfiltered set,
    // so that the green highlight reflects the absolute market best.
    const fullMatch = allMatches.find((m) => m.match_id === match.match_id) || match;
    const hasTotals = (match.totals || []).length > 0;

    return (
        <div
            className="card-elev p-5 sm:p-6"
            data-testid={`cote-match-${match.match_id}`}
        >
            <div className="mb-4">
                <span className="chip">{match.competition}</span>
                <div className="mt-3 font-heading font-bold text-white text-lg leading-tight">
                    {match.home}{" "}
                    <span className="text-gray-500 mx-1 font-normal">vs</span>{" "}
                    {match.away}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                    {match.kickoff}
                </div>
            </div>

            {/* 1N2 */}
            <div className="mb-4">
                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Vainqueur (1N2)
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                                <th className="text-left font-medium pb-2">
                                    Book
                                </th>
                                <th className="text-center font-medium pb-2">
                                    1
                                </th>
                                <th className="text-center font-medium pb-2">
                                    N
                                </th>
                                <th className="text-center font-medium pb-2">
                                    2
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {match.odds.map((o) => (
                                <tr
                                    key={o.bookmaker}
                                    className="border-t border-[#2A4A6B]"
                                >
                                    <td className="py-2 pr-2">
                                        <BookCell slug={o.bookmaker} />
                                    </td>
                                    <td className="py-2 px-1">
                                        <OddCell
                                            value={o.home}
                                            isBest={
                                                o.home === fullMatch.best_home
                                            }
                                        />
                                    </td>
                                    <td className="py-2 px-1">
                                        <OddCell
                                            value={o.draw}
                                            isBest={
                                                o.draw === fullMatch.best_draw
                                            }
                                        />
                                    </td>
                                    <td className="py-2 px-1">
                                        <OddCell
                                            value={o.away}
                                            isBest={
                                                o.away === fullMatch.best_away
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Over/Under */}
            {hasTotals && (
                <div className="pt-4 border-t border-[#2A4A6B]">
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                        Plus / Moins de{" "}
                        {match.totals[0]?.point?.toFixed(1) || "2.5"} buts
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                                    <th className="text-left font-medium pb-2">
                                        Book
                                    </th>
                                    <th className="text-center font-medium pb-2">
                                        +2.5
                                    </th>
                                    <th className="text-center font-medium pb-2">
                                        −2.5
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {match.totals.map((t) => (
                                    <tr
                                        key={t.bookmaker}
                                        className="border-t border-[#2A4A6B]"
                                    >
                                        <td className="py-2 pr-2">
                                            <BookCell slug={t.bookmaker} />
                                        </td>
                                        <td className="py-2 px-1">
                                            <OddCell
                                                value={t.over}
                                                isBest={
                                                    t.over ===
                                                    fullMatch.best_over
                                                }
                                            />
                                        </td>
                                        <td className="py-2 px-1">
                                            <OddCell
                                                value={t.under}
                                                isBest={
                                                    t.under ===
                                                    fullMatch.best_under
                                                }
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <div className="mt-5 pt-4 border-t border-[#2A4A6B] flex items-center justify-between">
                <span className="text-xs text-gray-500">
                    Cotes décimales · mises à jour live
                </span>
            </div>
        </div>
    );
};

const BookCell = ({ slug }) => {
    const b = getBookmakerBySlug(slug);
    if (!b) {
        // Fallback for FR-licensed books not in our top 6 (e.g. PMU). Auto-style.
        const fallbackColor = {
            pmu: "#1B4D8C",
        };
        const name = slug === "pmu" ? "PMU" : slug.toUpperCase();
        const fallbackUrl = slug === "pmu" ? "https://www.pmu.fr" : "#";
        return (
            <a
                href={fallbackUrl}
                target="_blank"
                rel="sponsored nofollow noopener"
                className="inline-block hover:opacity-85 transition-opacity"
            >
                <BrandBadge
                    name={name}
                    color={fallbackColor[slug] || "#475569"}
                    size="sm"
                />
            </a>
        );
    }
    return (
        <a
            href={b.affiliateUrl}
            target="_blank"
            rel="sponsored nofollow noopener"
            className="inline-block hover:opacity-85 transition-opacity"
        >
            <BrandBadge name={b.name} color={b.color} size="sm" />
        </a>
    );
};

export default Cotes;
